import React from "react";

import { NormalizedWithheldGame, WithheldReasonCode } from "../types";
import { formatGameDateTimeEt, formatGameLocalTime } from "../../../shared/utils/formatters";

interface WithheldGameCardProps {
  game: NormalizedWithheldGame;
}

const REASON_COPY: Record<WithheldReasonCode, string> = {
  awaiting_lineups: "Official starting lineups are not complete yet.",
  awaiting_named_starters: "Starting pitchers are still being finalized.",
  awaiting_lineups_and_starters: "Lineups and starting pitchers are still being finalized.",
  insufficient_feature_completeness: "Data quality checks are still being completed for this game.",
};

const EXPECTATION_COPY = "We refresh this board every 15 minutes as new official data arrives.";

function toReasonText(code: WithheldReasonCode): string {
  return REASON_COPY[code] ?? "Official game inputs are still being finalized.";
}

function getMatchupText(game: NormalizedWithheldGame): string {
  if (game.awayTeam && game.homeTeam) {
    return `${game.awayTeam} @ ${game.homeTeam}`;
  }

  if (game.awayTeam) {
    return `${game.awayTeam} @ Home Team TBD`;
  }

  if (game.homeTeam) {
    return `Away Team TBD @ ${game.homeTeam}`;
  }

  return `Game ${game.gameId}`;
}

export function WithheldGameCard({ game }: WithheldGameCardProps): JSX.Element {
  const reasonText = toReasonText(game.statusMessageCode);
  const localGameTime = formatGameLocalTime(game.gameStartTimeUtc, game.venueTimeZone);

  return (
    <article className="withheld-card" aria-label={`Awaiting official updates for ${getMatchupText(game)}`}>
      <header className="withheld-card-header">
        <h4 className="withheld-card-title">{getMatchupText(game)}</h4>
        <div className="withheld-card-time-block">
          <time className="withheld-card-time">{formatGameDateTimeEt(game.gameDate, game.gameStartTimeUtc)}</time>
          {localGameTime && <div className="withheld-card-local-time">{localGameTime}</div>}
        </div>
      </header>

      <p className="withheld-card-message">{reasonText}</p>

      <div className="withheld-card-chips" aria-label="Readiness indicators">
        <span className={`withheld-chip ${game.lineupsConfirmedAtRun ? "withheld-chip-ready" : "withheld-chip-awaiting"}`}>
          {game.lineupsConfirmedAtRun ? "Lineups Confirmed" : "Awaiting Lineups"}
        </span>
        <span className={`withheld-chip ${game.namedStartersPresent ? "withheld-chip-ready" : "withheld-chip-awaiting"}`}>
          {game.namedStartersPresent ? "Starters Announced" : "Awaiting Starters"}
        </span>
        <span className="withheld-chip withheld-chip-neutral">
          Slots {game.awayLineupSlotCount}/{game.homeLineupSlotCount}
        </span>
      </div>

      <p className="withheld-card-meta">{EXPECTATION_COPY}</p>
    </article>
  );
}
